import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { DollarSign, Mail, CreditCard, Copy, Check, Eye, Pencil, RotateCcw, RefreshCw } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

const SendBalanceDueDialog = ({ dialog, setDialog, onSend }) => {
  const quote = dialog.quote;
  const [copied, setCopied] = useState(false);
  const [editingBalance, setEditingBalance] = useState(false);
  const [overrideInput, setOverrideInput] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  // Authoritative numbers fetched from backend (base amounts derived from
  // payment_transactions — excludes the 3% Stripe fee from the paid total)
  const [amounts, setAmounts] = useState(null); // { quote_total_base, paid_base, balance_due_base, cc_fee, total_to_charge }
  const [loadingAmounts, setLoadingAmounts] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [justResynced, setJustResynced] = useState(false);

  const payBalanceLink = quote ? `${window.location.origin}/api/quotes/${quote.id}/pay-balance` : "";

  // Fetch authoritative base amounts whenever the dialog opens for a new quote
  useEffect(() => {
    if (!dialog.open || !quote?.id) return;
    let cancelled = false;
    (async () => {
      setLoadingAmounts(true);
      setOverrideInput("");
      setEditingBalance(false);
      setJustResynced(false);
      try {
        const res = await axios.post(`${API}/quotes/${quote.id}/balance-due-email-preview`, {
          origin_url: window.location.origin,
        });
        if (!cancelled) setAmounts(res.data);
      } catch {
        if (!cancelled) setAmounts(null);
      } finally {
        if (!cancelled) setLoadingAmounts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dialog.open, quote?.id]);

  // Override (admin-entered) wins; otherwise show computed base
  const overrideNum = overrideInput === "" ? null : parseFloat(overrideInput);
  const hasValidOverride = overrideNum !== null && !Number.isNaN(overrideNum) && overrideNum > 0;
  const quoteTotal = amounts?.quote_total_base ?? 0;
  const paidAmount = amounts?.paid_base ?? 0;
  const computedBalanceDue = amounts?.balance_due_base ?? 0;
  const balanceDue = hasValidOverride ? overrideNum : computedBalanceDue;
  const ccFee = Math.floor(balanceDue * 0.03 * 100) / 100;
  const totalToCharge = Math.floor((balanceDue + ccFee) * 100) / 100;

  if (!quote) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(payBalanceLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = payBalanceLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await axios.post(
        `${API}/quotes/${quote.id}/balance-due-email-preview`,
        {
          override_amount: hasValidOverride ? overrideNum : null,
          origin_url: window.location.origin,
        }
      );
      setPreviewHtml(res.data.html || "");
    } catch (err) {
      setPreviewHtml(
        `<div style="padding:20px;color:#dc2626;">Failed to load preview: ${err?.response?.data?.detail || err.message}</div>`
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = () => {
    onSend({ override_amount: hasValidOverride ? overrideNum : null });
  };

  // Detect a drifted/stale payment_schedule — either the sum of installment amounts
  // doesn't match the quote total, OR a "paid" installment's amount differs from
  // what was actually paid (i.e. quote was edited after a deposit was charged)
  const hasStaleSchedule = (() => {
    if (justResynced) return false;
    const schedule = quote.payment_schedule || [];
    if (!schedule.length || !amounts) return false;
    const scheduleSum = schedule.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    if (Math.abs(scheduleSum - (amounts.quote_total_base || 0)) > 1) return true;
    const paidSumInSchedule = schedule
      .filter((i) => i.paid)
      .reduce((s, i) => s + (Number(i.amount) || 0), 0);
    return Math.abs(paidSumInSchedule - (amounts.paid_base || 0)) > 1;
  })();

  const handleResync = async () => {
    if (!quote?.id) return;
    if (!window.confirm(
      "Re-sync the payment schedule from actual payment history?\n\n" +
      "This clears the stale schedule on this quote and recalculates the balance " +
      "from real payments. Safe — no money moves."
    )) return;
    setResyncing(true);
    try {
      const r = await axios.post(`${API}/admin/quotes/${quote.id}/resync-payment-schedule`);
      toast.success(r.data?.message || "Payment schedule re-synced");
      setJustResynced(true);
      const res = await axios.post(`${API}/quotes/${quote.id}/balance-due-email-preview`, {
        origin_url: window.location.origin,
      });
      setAmounts(res.data);
      setOverrideInput("");
      setEditingBalance(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-sync failed");
    } finally {
      setResyncing(false);
    }
  };

  return (
    <Dialog open={dialog.open} onOpenChange={(open) => setDialog((p) => ({ ...p, open }))}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#d97706]" />
            Send Balance Due
          </DialogTitle>
          <DialogDescription>
            Send a payment link to the customer for their remaining balance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 mt-2 min-w-0">
          {/* Quote Info */}
          <div className="bg-zinc-50 rounded-lg p-4 space-y-2 min-w-0">
            <div className="flex justify-between gap-3 text-sm min-w-0">
              <span className="text-zinc-500 shrink-0">Quote</span>
              <span className="font-semibold truncate text-right">{quote.quote_number}</span>
            </div>
            <div className="flex justify-between gap-3 text-sm min-w-0">
              <span className="text-zinc-500 shrink-0">Customer</span>
              <span className="font-medium truncate text-right">{quote.customer_name}</span>
            </div>
            <div className="flex justify-between gap-3 text-sm min-w-0">
              <span className="text-zinc-500 shrink-0">Email</span>
              <span className="text-zinc-700 truncate text-right">{quote.customer_email || "No email"}</span>
            </div>
          </div>

          {/* Balance Info */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-900">Balance Summary</span>
              {loadingAmounts && (
                <span className="w-3 h-3 border border-amber-400 border-t-transparent rounded-full animate-spin ml-1" />
              )}
            </div>
            <div className="space-y-1.5">
              {amounts?.discount_amount > 0 && (
                <>
                  <div className="flex justify-between gap-3 text-sm">
                    <span className="text-amber-700">Subtotal</span>
                    <span className="font-medium text-amber-900 tabular-nums">${(amounts.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between gap-3 text-sm" data-testid="dialog-discount-row">
                    <span className="text-amber-700">Discount</span>
                    <span className="font-medium text-green-700 tabular-nums">-${(amounts.discount_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between gap-3 text-sm">
                <span className="text-amber-700">Quote Total</span>
                <span className="font-medium text-amber-900 tabular-nums">${quoteTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              {/* Change orders rolled into the balance */}
              {(amounts?.change_orders || []).map((co) => (
                <div
                  key={co.id}
                  className="flex justify-between gap-3 text-[11px] text-amber-700 pl-4"
                  data-testid={`balance-co-${co.id}`}
                >
                  <span className="truncate">+ {co.change_order_number}{co.description ? ` · ${co.description}` : ""}</span>
                  <span className="tabular-nums shrink-0">+${(co.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="flex justify-between gap-3 text-sm">
                <span className="text-amber-700">Less Amount Paid</span>
                <span className="font-medium text-green-700 tabular-nums">-${paidAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              {/* Individual prior payment lines with date + method */}
              {(amounts?.payments || []).map((p, idx) => {
                const d = p.date ? new Date(p.date) : null;
                const dateStr = d && !Number.isNaN(d.getTime())
                  ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                  : "";
                const methodLabel = p.method === "card" ? "Card" : (p.method || "Cash").replace("_", "/");
                return (
                  <div
                    key={`pmt-${idx}`}
                    className="flex justify-between gap-3 text-[11px] text-amber-700 pl-4"
                    data-testid={`prior-payment-${idx}`}
                  >
                    <span className="truncate">└ {methodLabel}{dateStr ? ` · ${dateStr}` : ""}</span>
                    <span className="tabular-nums shrink-0">-${(p.base_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                );
              })}
              <div className="flex justify-between gap-3 text-sm pt-2 border-t border-amber-200 items-center">
                <span className="font-semibold text-amber-900 shrink-0">Balance Due</span>
                {editingBalance ? (
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="text-amber-900 font-bold">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      autoFocus
                      value={overrideInput}
                      onChange={(e) => setOverrideInput(e.target.value)}
                      placeholder={computedBalanceDue.toFixed(2)}
                      className="h-8 w-28 text-right font-bold text-amber-900 bg-white"
                      data-testid="balance-due-override-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      title="Reset to computed amount"
                      onClick={() => { setOverrideInput(""); }}
                      className="h-7 w-7 p-0 text-amber-700 hover:bg-amber-100"
                      data-testid="balance-due-reset-btn"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingBalance(true)}
                    className="group flex items-center gap-1.5 text-amber-900 hover:bg-amber-100 rounded px-1.5 py-0.5 -mr-1.5 transition-colors"
                    data-testid="balance-due-edit-btn"
                    title="Adjust balance due amount"
                  >
                    <span className="font-semibold text-base tabular-nums">${balanceDue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    <Pencil className="w-3 h-3 text-amber-600 opacity-50 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </div>
              {hasValidOverride && (
                <p className="text-[11px] text-amber-700 italic pt-0.5 text-right" data-testid="balance-due-override-hint">
                  Manual override — replaces computed ${computedBalanceDue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
              )}
              {/* Card processing fee breakdown */}
              <div className="flex justify-between gap-3 text-xs text-amber-700 pt-1">
                <span>+ 3% Card processing fee</span>
                <span className="tabular-nums">+ ${ccFee.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between gap-3 text-sm pt-1.5 border-t border-amber-200" data-testid="total-to-charge-row">
                <span className="font-semibold text-amber-900">Total to Charge Card</span>
                <span className="font-bold text-lg text-[#d97706] tabular-nums">${totalToCharge.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* Copy Link */}
          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 min-w-0">
            <p className="text-xs text-zinc-500 mb-2 font-medium uppercase tracking-wide">Direct Payment Link</p>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-zinc-600 truncate min-w-0 flex-1 font-mono bg-white border border-zinc-200 rounded px-2 py-1">
                {payBalanceLink}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                className="shrink-0 gap-1.5"
                data-testid="copy-balance-link-btn"
              >
                {copied ? <><Check className="w-3.5 h-3.5 text-green-600" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
              </Button>
            </div>
          </div>

          <p className="text-sm text-zinc-500">
            The customer will receive an email with a link to pay the remaining balance via credit card.
          </p>

          {/* Stale-schedule banner — appears when payment_schedule installment amounts don't match the quote */}
          {hasStaleSchedule && (
            <div
              className="bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-3"
              data-testid="stale-schedule-banner"
            >
              <RefreshCw className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-900">Payment schedule out of sync</p>
                <p className="text-xs text-red-700 mt-1">
                  This quote&apos;s stored payment schedule doesn&apos;t match the actual quote total — likely from an edit after the quote was sent. Re-sync to fix it so Stripe charges the right amount.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleResync}
                  disabled={resyncing}
                  className="mt-2 h-7 text-xs border-red-300 text-red-700 hover:bg-red-100 gap-1.5"
                  data-testid="resync-schedule-btn"
                >
                  {resyncing ? (
                    <><span className="w-3 h-3 border border-red-500 border-t-transparent rounded-full animate-spin" /> Re-syncing…</>
                  ) : (
                    <><RefreshCw className="w-3 h-3" /> Re-sync payment schedule</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              className="gap-2"
              data-testid="preview-balance-due-email-btn"
            >
              <Eye className="w-4 h-4" />
              Preview Email
            </Button>
            <Button variant="outline" onClick={() => setDialog((p) => ({ ...p, open: false }))}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={dialog.sending || !quote.customer_email}
              className="bg-[#d97706] hover:bg-[#b45309] text-white"
              data-testid="confirm-send-balance-due"
            >
              {dialog.sending ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <><Mail className="w-4 h-4 mr-2" />Send Balance Due Email</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Email preview modal — opens on top of the dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-zinc-700" />
              Email Preview
            </DialogTitle>
            <DialogDescription>
              Exactly what {quote.customer_name} will receive at {quote.customer_email || "—"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-zinc-100 p-4" data-testid="email-preview-body">
            {previewLoading ? (
              <div className="flex items-center justify-center h-64 text-zinc-500">
                <span className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin mr-2" />
                Rendering preview...
              </div>
            ) : (
              <iframe
                title="Balance Due Email Preview"
                srcDoc={previewHtml}
                sandbox=""
                className="w-full h-[70vh] bg-white rounded shadow-sm border border-zinc-200"
                data-testid="email-preview-iframe"
              />
            )}
          </div>
          <div className="flex justify-end gap-3 px-6 py-3 border-t bg-white">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close Preview
            </Button>
            <Button
              onClick={() => { setPreviewOpen(false); handleSend(); }}
              disabled={dialog.sending || !quote.customer_email}
              className="bg-[#d97706] hover:bg-[#b45309] text-white gap-2"
              data-testid="send-from-preview-btn"
            >
              <Mail className="w-4 h-4" />
              Send This Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default SendBalanceDueDialog;
